import { useRef, useCallback, useEffect, useState } from 'react';

interface SignaturePadProps {
  /** Called with a PNG data URL whenever the user exports. */
  onChange?: (dataUrl: string) => void;
  /** PNG data URL to display (existing signature/stamp). Must be a data URL, not a fetch URL. */
  dataUrl?: string;
  height?: number;
  label?: string;
  /** Increment to force a full reset (fresh canvas, no image). Used when the parent clears. */
  clearKey?: number;
}

/**
 * Canvas-based signature/stamp drawing pad.
 * Supports mouse and touch, exports a transparent PNG data URL.
 * Can display an existing image passed as a data URL.
 */
export function SignaturePad({ onChange, dataUrl, height = 160, label = '', clearKey = 0 }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const drawing = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const [isEmpty, setIsEmpty] = useState(!dataUrl);

  // Reset the canvas (clear image + mark empty) whenever clearKey increments.
  // Only actually clears if there's no dataUrl to display — protects against a
  // race where clearKey and dataUrl change in the same render (e.g. clearing).
  useEffect(() => {
    if (!clearKey || clearKey === 0 || dataUrl) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setIsEmpty(true);
  }, [clearKey, dataUrl]);

  // Load an existing image (data URL) onto the canvas whenever it changes.
  // Runs on mount and whenever dataUrl changes (e.g. after the ImageCapture
  // fetches the API URL and converts it to a data URL).
  useEffect(() => {
    if (!dataUrl) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) drawImageToCanvas(img);
    };
    img.src = dataUrl;
    return () => { cancelled = true; };
  }, [dataUrl]);

  const getCanvasPos = useCallback((e: MouseEvent | TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  const drawImageToCanvas = useCallback((img: HTMLImageElement) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // The resize effect applies a DPR scale transform to the context so that
    // drawing commands use CSS-pixel coordinates. Reset to identity here so we
    // can draw in raw canvas-pixel coordinates (canvas.width/height are already
    // scaled to the device pixel ratio), then restore the DPR transform.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // White background for visual clarity while drawing.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Scale image to fit canvas while preserving aspect ratio.
    const scaleX = canvas.width / img.width;
    const scaleY = canvas.height / img.height;
    const scale = Math.min(scaleX, scaleY, 1);
    const w = img.width * scale;
    const h = img.height * scale;
    const x = (canvas.width - w) / 2;
    const y = (canvas.height - h) / 2;
    ctx.drawImage(img, x, y, w, h);
    ctx.restore();

    setIsEmpty(false);
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    drawing.current = true;
    setIsEmpty(false);
    const pos = getCanvasPos(e.nativeEvent);
    if (pos) lastPos.current = pos;
  }, [getCanvasPos]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const pos = getCanvasPos(e.nativeEvent);
    if (!pos || !lastPos.current) return;

    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  }, [getCanvasPos]);

  const emitChange = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onChange || isEmpty) return;
    const dataUrl = canvas.toDataURL('image/png');
    onChange(dataUrl);
  }, [onChange, isEmpty]);

  const stopDrawing = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;
    lastPos.current = null;
    emitChange();
  }, [emitChange]);

  // Set canvas resolution to match display size (retina-aware).
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${height}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Scale so drawing commands use CSS-pixel coordinates.
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        // Fill white background.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    return () => ro.disconnect();
  }, [height]);

  return (
    <div>
      {label && <label className="label">{label}</label>}
      <div ref={containerRef} className="relative rounded-lg border border-gray-300 overflow-hidden" style={{ height }}>
        <canvas
          ref={canvasRef}
          className="w-full cursor-crosshair touch-none"
          style={{ height: `${height}px` }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {isEmpty && !drawing.current && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-gray-400 text-sm">Draw your {label.toLowerCase()} here</span>
          </div>
        )}
      </div>
    </div>
  );
}
